<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M4.
 * The agent-facing Copilot panel: shows the thread, sends the agent's questions to EvaCopilotAPI, and
 * appends streamed replies pushed over ActionCable (eva.copilot.message.created). Visual language is
 * the upstream Captain copilot idiom (SidebarActionsHeader, editorial label rows, iris AI accent);
 * Eva's identity is the spark — the same i-lucide-sparkles that marks the Copilot sidepanel tab.
 * All copy lives under EVA_COPILOT in fork.json (pt-BR first; Captain's copilot has no pt-BR).
 */
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaCopilotAPI from 'dashboard/api/eva/copilot';
import { useUISettings } from 'dashboard/composables/useUISettings';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import EvaCopilotAssistantMessage from 'dashboard/components-next/Eva/CopilotAssistantMessage.vue';
import EvaCopilotThinkingSteps from 'dashboard/components-next/Eva/CopilotThinkingSteps.vue';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
  conversationInboxType: { type: String, default: '' },
  accountId: { type: [Number, String], default: null },
});

const { t } = useI18n();
const { updateUISettings } = useUISettings();

const messages = ref([]);
const draft = ref('');
const threadId = ref(null);
const isSending = ref(false);
// Rows broadcast for a thread no session knows yet (the reply job races the POST response that
// carries the thread id) — held here until some session learns its id, then claimed, so a fast
// first reply is never dropped.
const orphanedRows = ref([]);
// Bumped by reset, conversation switches and account switches: a createThread still in flight must
// not write its stale thread id into a panel that has since moved on. Reset additionally records
// its epoch as abandoned — a switched-away send is adopted by its conversation's cached session,
// a reset one is dropped for good.
let sendEpoch = 0;
const abandonedEpochs = new Set();

// Session cache: each conversation keeps its own copilot exchange while the panel stays mounted,
// so switching conversations and returning restores the thread instead of wiping it. Keys are
// conversation display ids, which are per-account — the accountId watcher below clears the cache
// so another account's exchange can never restore here. Threads are not conversation-bound
// server-side, so this survives navigation but not a page reload.
const sessionByConversation = new Map();
const sessionKey = id => id ?? 'none';

const chatContainer = ref(null);
const textareaRef = ref(null);

const hasMessages = computed(() => messages.value.length > 0);

// Consecutive assistant_thinking rows collapse into one steps group between answer turns
// (upstream grouping), so tool provenance stays available without crowding the feed.
const groupedMessages = computed(() => {
  const result = [];
  let thinkingGroup = [];
  const flushThinking = () => {
    if (!thinkingGroup.length) return;
    result.push({
      id: `thinking-${thinkingGroup[0].id}`,
      message_type: 'thinking_group',
      messages: thinkingGroup,
    });
    thinkingGroup = [];
  };
  messages.value.forEach(message => {
    if (message.message_type === 'assistant_thinking') {
      thinkingGroup.push(message);
    } else {
      flushThinking();
      result.push(message);
    }
  });
  flushThinking();
  return result;
});

// The reply job answers every user turn with exactly one assistant row (refusals and errors
// included), so Eva is still working while user turns outnumber assistant answers — a plain
// last-turn check would drop the loader when an earlier answer lands while a later prompt is
// still queued. Thinking rows are ignored: cable broadcasts are async jobs, so a delayed
// thinking row can land AFTER the final answer — counting it would re-arm the loader with
// nothing left to clear it.
const awaitingReply = computed(() => {
  let pending = 0;
  messages.value.forEach(message => {
    if (message.message_type === 'user') pending += 1;
    else if (message.message_type === 'assistant')
      pending = Math.max(0, pending - 1);
  });
  return pending > 0;
});

const PROMPT_OPTIONS = [
  {
    label: 'EVA_COPILOT.PROMPTS.SUMMARIZE.LABEL',
    content: 'EVA_COPILOT.PROMPTS.SUMMARIZE.CONTENT',
  },
  {
    label: 'EVA_COPILOT.PROMPTS.SUGGEST.LABEL',
    content: 'EVA_COPILOT.PROMPTS.SUGGEST.CONTENT',
  },
  {
    label: 'EVA_COPILOT.PROMPTS.NEXT_STEPS.LABEL',
    content: 'EVA_COPILOT.PROMPTS.NEXT_STEPS.CONTENT',
  },
];

const headerButtons = computed(() => {
  if (!hasMessages.value) return [];
  return [
    {
      key: 'reset',
      icon: 'i-lucide-refresh-ccw',
      tooltip: t('EVA_COPILOT.RESET'),
    },
  ];
});

const scrollToBottom = async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

watch(messages, scrollToBottom, { deep: true });

watch(
  () => props.conversationId,
  (newId, oldId) => {
    sessionByConversation.set(sessionKey(oldId), {
      threadId: threadId.value,
      messages: messages.value,
      isSending: isSending.value,
    });
    const restored = sessionByConversation.get(sessionKey(newId));
    sendEpoch += 1;
    threadId.value = restored?.threadId ?? null;
    messages.value = restored?.messages ?? [];
    draft.value = '';
    // A restored session whose send is still in flight keeps the composer locked — a second
    // prompt while the first createThread is unresolved would fork a second server thread.
    isSending.value = restored?.isSending ?? false;
  }
);

// Conversation display ids restart per account, so a cached session (and its thread ids) from the
// previous account must never restore into this one — drop everything on an account switch.
watch(
  () => props.accountId,
  () => {
    sessionByConversation.clear();
    orphanedRows.value = [];
    sendEpoch += 1;
    threadId.value = null;
    messages.value = [];
    draft.value = '';
    isSending.value = false;
  }
);

const adjustComposerHeight = () => {
  if (!textareaRef.value) return;
  textareaRef.value.style.height = 'auto';
  textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`;
};

// Move the buffered rows belonging to a just-learned thread id into that thread's feed.
const claimOrphanedRows = (id, target) => {
  orphanedRows.value
    .filter(row => row.copilot_thread_id === id)
    .forEach(row => target.push(row));
  orphanedRows.value = orphanedRows.value.filter(
    row => row.copilot_thread_id !== id
  );
};

const buildErrorRow = (rows, error) => ({
  id: `error-${rows.length}`,
  message_type: 'assistant',
  message: {
    content: error?.response?.data?.error || t('EVA_COPILOT.REQUEST_ERROR'),
  },
});

const sendMessage = async () => {
  const content = draft.value.trim();
  if (!content || isSending.value) return;

  isSending.value = true;
  messages.value.push({
    id: `pending-${messages.value.length}`,
    message_type: 'user',
    message: { content },
  });
  draft.value = '';
  nextTick(adjustComposerHeight);
  const payload = { message: content, conversationId: props.conversationId };
  const epoch = sendEpoch;
  const originKey = sessionKey(props.conversationId);
  // A stale epoch means the panel moved on mid-flight. A switch cached this conversation's
  // session, so the resolved thread (or error) is adopted there — the agent finds the outcome
  // when they return. A reset abandoned the exchange for good.
  const cachedOrigin = () =>
    abandonedEpochs.has(epoch) ? null : sessionByConversation.get(originKey);
  try {
    if (threadId.value) {
      await EvaCopilotAPI.createMessage(threadId.value, payload);
    } else {
      const { data } = await EvaCopilotAPI.createThread(payload);
      if (epoch === sendEpoch) {
        threadId.value = data.id;
        claimOrphanedRows(data.id, messages.value);
      } else {
        const cached = cachedOrigin();
        if (cached && cached.threadId === null) {
          cached.threadId = data.id;
          claimOrphanedRows(data.id, cached.messages);
          // The agent may have already returned to this conversation (the restore shares the
          // cached messages array, so the transcript on screen is this very exchange) — the
          // live thread id must follow, or the next prompt would fork a brand-new thread and
          // lose the context.
          if (
            sessionKey(props.conversationId) === originKey &&
            threadId.value === null
          ) {
            threadId.value = data.id;
          }
        }
      }
    }
  } catch (error) {
    // A rejected request (kill switch, spend cap, network) would otherwise leave the agent's
    // question hanging with no reply — surface the server's reason, or a generic fallback.
    if (epoch === sendEpoch) {
      messages.value.push(buildErrorRow(messages.value, error));
    } else {
      const cached = cachedOrigin();
      cached?.messages.push(buildErrorRow(cached.messages, error));
    }
  } finally {
    if (epoch === sendEpoch) {
      isSending.value = false;
    } else {
      const cached = cachedOrigin();
      if (cached) {
        cached.isSending = false;
        // ...and the live flag too when the agent is already back on the origin conversation
        // (the restore re-armed it from this cached session).
        if (sessionKey(props.conversationId) === originKey) {
          isSending.value = false;
        }
      }
    }
    // This send has settled; its epoch is never consulted again. Drop it so a reset marked as
    // abandoned mid-flight can't accumulate in the set for the panel's whole mounted lifetime.
    abandonedEpochs.delete(epoch);
  }
};

const useSuggestion = option => {
  draft.value = t(option.content);
  sendMessage();
};

const resetThread = () => {
  abandonedEpochs.add(sendEpoch);
  sendEpoch += 1;
  threadId.value = null;
  messages.value = [];
  isSending.value = false;
  // Discard this conversation's cached session too: a first send still in flight from before a
  // switch-away-and-back holds an epoch OLDER than the one just abandoned, so the epoch guard alone
  // would let its resolved thread id adopt back into (and stream a ghost answer into) the panel we
  // just reset. Dropping the cache entry the resolve reads (cachedOrigin) closes that path.
  sessionByConversation.delete(sessionKey(props.conversationId));
};

const handleHeaderAction = action => {
  if (action === 'reset') resetThread();
};

const closePanel = () => {
  updateUISettings({
    is_copilot_panel_open: false,
    is_contact_sidebar_open: false,
  });
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  sendMessage();
};

const handleComposerInput = () => nextTick(adjustComposerHeight);

// Wired to the ActionCable event by the host; appends a row to the exchange it belongs to. The
// agent's own user rows also broadcast back — skip them, the local echo pushed by sendMessage
// already renders that turn (keeping both duplicated every question in the visible feed). A row
// for a conversation the agent switched away from lands in its cached session (so the answer is
// there when they return); rows for a thread no session knows yet are buffered and claimed once
// a createThread response reveals the id.
const appendStreamedMessage = streamed => {
  if (streamed.message_type === 'user') return;
  if (
    threadId.value !== null &&
    streamed.copilot_thread_id === threadId.value
  ) {
    messages.value.push(streamed);
    return;
  }
  const cached = [...sessionByConversation.values()].find(
    session => session.threadId === streamed.copilot_thread_id
  );
  if (cached) {
    cached.messages.push(streamed);
    return;
  }
  orphanedRows.value.push(streamed);
};

defineExpose({ appendStreamedMessage });
</script>

<template>
  <div class="flex h-full w-full flex-col text-sm leading-6 tracking-tight">
    <SidebarActionsHeader
      :title="t('EVA_COPILOT.TITLE')"
      :buttons="headerButtons"
      @click="handleHeaderAction"
      @close="closePanel"
    />
    <div
      ref="chatContainer"
      class="flex flex-1 items-start overflow-y-auto px-4 py-4"
    >
      <div v-if="hasMessages" class="flex w-full flex-1 flex-col space-y-6">
        <template v-for="item in groupedMessages" :key="item.id">
          <div
            v-if="item.message_type === 'user'"
            class="space-y-1 text-n-slate-12"
          >
            <div class="font-medium">{{ t('EVA_COPILOT.YOU') }}</div>
            <div class="break-words">{{ item.message.content }}</div>
          </div>
          <EvaCopilotAssistantMessage
            v-else-if="item.message_type === 'assistant'"
            :message="item.message"
            :conversation-inbox-type="conversationInboxType"
          />
          <EvaCopilotThinkingSteps v-else :messages="item.messages" />
        </template>
        <div
          v-if="awaitingReply"
          data-test-id="eva-copilot-loader"
          class="flex items-center gap-2"
        >
          <span class="font-medium text-n-iris-11">
            {{ t('EVA_COPILOT.LOADER') }}
          </span>
          <div class="flex gap-1">
            <div
              class="size-2 animate-bounce rounded-full bg-n-iris-9 [animation-delay:-0.3s]"
            />
            <div
              class="size-2 animate-bounce rounded-full bg-n-iris-9 [animation-delay:-0.15s]"
            />
            <div class="size-2 animate-bounce rounded-full bg-n-iris-9" />
          </div>
        </div>
      </div>
      <div v-else class="flex flex-1 flex-col gap-6 px-2">
        <div class="flex flex-col gap-4 py-4">
          <div
            class="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-n-iris-9 to-n-blue-9 shadow-sm"
          >
            <i class="i-lucide-sparkles size-5 text-white" />
          </div>
          <div class="space-y-1">
            <h3 class="text-base font-medium leading-8 text-n-slate-12">
              {{ t('EVA_COPILOT.PANEL_TITLE') }}
            </h3>
            <p class="mb-0 text-sm leading-6 text-n-slate-11">
              {{ t('EVA_COPILOT.KICK_OFF_MESSAGE') }}
            </p>
          </div>
        </div>
        <div class="w-full space-y-2">
          <span class="block text-xs text-n-slate-10">
            {{ t('EVA_COPILOT.TRY_THESE_PROMPTS') }}
          </span>
          <div class="space-y-1">
            <button
              v-for="option in PROMPT_OPTIONS"
              :key="option.label"
              type="button"
              class="flex w-full items-center justify-between rounded-md border border-n-weak bg-n-slate-2 px-3 py-2 text-n-slate-11 transition-colors hover:bg-n-slate-3"
              @click="useSuggestion(option)"
            >
              <span>{{ t(option.label) }}</span>
              <i class="i-lucide-chevron-right size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <form class="relative mx-3 mb-2 mt-px" @submit.prevent="sendMessage">
      <textarea
        ref="textareaRef"
        v-model="draft"
        rows="1"
        :placeholder="t('EVA_COPILOT.PLACEHOLDER')"
        class="reset-base mb-0 max-h-[200px] w-full resize-none overflow-hidden rounded-lg border border-n-weak bg-n-alpha-3 py-3 text-sm text-n-slate-12 focus:border-n-iris-11 focus:outline-0 focus:outline-none focus:ring-2 focus:ring-n-iris-11 ltr:pl-4 ltr:pr-12 rtl:pl-12 rtl:pr-4"
        @input="handleComposerInput"
        @keydown.enter.exact="handleEnterKey"
      />
      <button
        type="submit"
        :disabled="isSending"
        :aria-label="t('EVA_COPILOT.SEND')"
        class="absolute top-1/2 flex h-9 w-10 -translate-y-1/2 items-center justify-center text-n-slate-11 hover:text-n-iris-11 disabled:opacity-50 ltr:right-1 rtl:left-1"
      >
        <i class="i-ph-arrow-up" />
      </button>
    </form>
  </div>
</template>
